// Payroll types — mirror the backend Prisma models and calculator output.

export type EmploymentType = 'FULL_TIME' | 'PART_TIME' | 'CONTRACT' | 'INTERN' | 'CONSULTANT';
export type EmployeeStatus = 'ACTIVE' | 'ON_LEAVE' | 'RESIGNED' | 'TERMINATED';
export type PayComponentType = 'EARNING' | 'DEDUCTION' | 'EMPLOYER_CONTRIBUTION';
export type PayComponentCalc =
  | 'FIXED'
  | 'PERCENT_OF_CTC'
  | 'PERCENT_OF_BASIC'
  | 'PERCENT_OF_GROSS'
  | 'BALANCE';

export interface PayComponent {
  id: string;
  code: string;
  label: string;
  type: PayComponentType;
  calc: PayComponentCalc;
  /** Decimal comes back as a string over the wire. */
  defaultValue: string | null;
  isBasic: boolean;
  isTaxable: boolean;
  isStatutory: boolean;
  proratable: boolean;
  description?: string | null;
  position: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SalaryStructureLine {
  id: string;
  structureId: string;
  componentId: string;
  valueOverride: string | null;
  position: number;
  component: PayComponent;
}

export type SalaryRevisionType =
  | 'INITIAL'
  | 'PROMOTION'
  | 'ANNUAL_INCREMENT'
  | 'CORRECTION'
  | 'ADJUSTMENT'
  | 'OTHER';

export interface SalaryStructure {
  id: string;
  employeeId: string;
  effectiveFrom: string;
  ctcAnnual: string;
  currency: string;
  notes?: string | null;
  revisionType: SalaryRevisionType;
  reason?: string | null;
  createdById?: string | null;
  createdAt: string;
  updatedAt: string;
  lines: SalaryStructureLine[];
  employee?: { id: string; employeeCode: string; firstName: string; lastName: string };
  createdBy?: { id: string; firstName: string; lastName: string } | null;

  // ── Derived by the API when listing an employee's history ────────────────
  /** CTC of the revision immediately before this one; null for the first. */
  previousCtcAnnual?: number | null;
  changeAmount?: number | null;
  changePercent?: number | null;
  /** The revision in force today. */
  isCurrent?: boolean;
  /** Effective date is in the future — not yet in force. */
  isScheduled?: boolean;
  payslipCount?: number;
  /** Used by a payslip, so frozen: add a new revision instead of editing. */
  isLocked?: boolean;
}

/** GET /employees/:id/salary-structures/backdate-impact */
export interface BackdateImpact {
  isBackdated: boolean;
  affectedPayslips: Array<{
    id: string;
    payslipNumber: string;
    status: PayslipStatus;
    periodMonth: number;
    periodYear: number;
    netPay: string;
    currency: string;
  }>;
  note: string | null;
}

export const SALARY_REVISION_TYPE_OPTIONS: { value: SalaryRevisionType; label: string; hint: string }[] = [
  { value: 'INITIAL', label: 'Initial', hint: 'The first salary on record for this employee.' },
  { value: 'PROMOTION', label: 'Promotion', hint: 'Pay change accompanying a change of role.' },
  { value: 'ANNUAL_INCREMENT', label: 'Annual increment', hint: 'Regular review cycle raise.' },
  { value: 'CORRECTION', label: 'Correction', hint: 'Fixing a mistake in an earlier revision.' },
  { value: 'ADJUSTMENT', label: 'Restructure', hint: 'Components rearranged with no change in total cost.' },
  { value: 'OTHER', label: 'Other', hint: 'Anything else — explain in the reason.' },
];

export interface Employee {
  id: string;
  employeeCode: string;
  firstName: string;
  lastName: string;
  email?: string | null;
  phone?: string | null;
  designation?: string | null;
  department?: string | null;
  employmentType: EmploymentType;
  status: EmployeeStatus;
  dateOfJoining: string;
  dateOfExit?: string | null;
  dateOfBirth?: string | null;
  taxIdentifier?: string | null;
  socialSecurityId?: string | null;
  pensionId?: string | null;
  bankName?: string | null;
  bankAccountName?: string | null;
  bankAccountNumber?: string | null;
  bankIfsc?: string | null;
  userId?: string | null;
  notes?: string | null;
  createdAt: string;
  updatedAt: string;
  user?: { id: string; email: string; role: string; isActive: boolean } | null;
  salaryStructures?: SalaryStructure[];
}

/** One computed line of a breakdown. */
export interface ComputedLine {
  code: string;
  label: string;
  type: PayComponentType;
  calc: PayComponentCalc;
  amount: number;
  isTaxable: boolean;
  isStatutory: boolean;
  proratable: boolean;
  position: number;
}

/** Output of the backend salary calculator (POST /pay-components/preview). */
export interface SalaryBreakdown {
  monthlyCtc: number;
  basic: number;
  grossEarnings: number;
  totalDeductions: number;
  employerContributions: number;
  netPay: number;
  /** monthlyCtc − (gross + employer). Non-zero when nothing absorbs the remainder. */
  ctcVariance: number;
  earnings: ComputedLine[];
  deductions: ComputedLine[];
  employerContribs: ComputedLine[];
  lines: ComputedLine[];
}

/** Editor row before it is saved — componentId plus an optional override. */
export interface StructureLineInput {
  componentId: string;
  valueOverride?: number;
  position?: number;
}

export const EMPLOYMENT_TYPE_OPTIONS: { value: EmploymentType; label: string }[] = [
  { value: 'FULL_TIME', label: 'Full time' },
  { value: 'PART_TIME', label: 'Part time' },
  { value: 'CONTRACT', label: 'Contract' },
  { value: 'INTERN', label: 'Intern' },
  { value: 'CONSULTANT', label: 'Consultant' },
];

export const EMPLOYEE_STATUS_OPTIONS: { value: EmployeeStatus; label: string }[] = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'ON_LEAVE', label: 'On leave' },
  { value: 'RESIGNED', label: 'Resigned' },
  { value: 'TERMINATED', label: 'Terminated' },
];

export const PAY_COMPONENT_TYPE_OPTIONS: { value: PayComponentType; label: string }[] = [
  { value: 'EARNING', label: 'Earning' },
  { value: 'DEDUCTION', label: 'Deduction' },
  { value: 'EMPLOYER_CONTRIBUTION', label: 'Employer contribution' },
];

export const PAY_COMPONENT_CALC_OPTIONS: { value: PayComponentCalc; label: string; hint: string }[] = [
  { value: 'FIXED', label: 'Fixed amount', hint: 'A flat monthly amount.' },
  { value: 'PERCENT_OF_CTC', label: '% of CTC', hint: 'Percentage of the monthly CTC.' },
  { value: 'PERCENT_OF_BASIC', label: '% of Basic', hint: 'Percentage of the basic component.' },
  { value: 'PERCENT_OF_GROSS', label: '% of Gross', hint: 'Percentage of gross earnings. Not allowed on an earning.' },
  { value: 'BALANCE', label: 'Balance', hint: 'Absorbs whatever CTC is left. One per structure, earnings only.' },
];

// ── Payslips ────────────────────────────────────────────────────────────────

export type PayslipStatus = 'DRAFT' | 'PUBLISHED' | 'PAID' | 'CANCELLED' | 'SUPERSEDED';

/** The two ends of a correction, as the detail endpoint returns them. */
export interface PayslipLink {
  id: string;
  payslipNumber: string;
  status: PayslipStatus;
  netPay: string;
  publishedAt?: string | null;
}

/** Every payslip ever issued for one employee-month, newest first. */
export interface PayslipPeriodHistory {
  employeeId: string;
  periodYear: number;
  periodMonth: number;
  /** The one currently holding the month — null if it was cancelled outright. */
  currentId: string | null;
  payslips: Array<{
    id: string;
    payslipNumber: string;
    status: PayslipStatus;
    netPay: string;
    publishedAt?: string | null;
    cancelledAt?: string | null;
    supersededAt?: string | null;
    supersedesId?: string | null;
    createdAt: string;
  }>;
}

export interface PayslipItem {
  id: string;
  payslipId: string;
  code: string;
  label: string;
  type: PayComponentType;
  /** Decimals arrive as strings over the wire. */
  amount: string;
  /** What the line would have been at full attendance. */
  fullAmount: string;
  isTaxable: boolean;
  isStatutory: boolean;
  position: number;
}

export interface Payslip {
  id: string;
  payslipNumber: string;
  status: PayslipStatus;
  employeeId: string;
  salaryStructureId?: string | null;

  periodStart: string;
  periodEnd: string;
  periodMonth: number;
  periodYear: number;
  payDate: string;

  totalDays: number;
  paidDays: string;
  lopDays: string;

  currency: string;
  ctcAnnual: string;
  grossEarnings: string;
  totalDeductions: string;
  employerContributions: string;
  netPay: string;

  // Frozen snapshot — what the payslip said on the day it was issued.
  employeeCode: string;
  employeeName: string;
  designation?: string | null;
  department?: string | null;
  dateOfJoining: string;
  taxIdentifier?: string | null;
  socialSecurityId?: string | null;
  pensionId?: string | null;
  bankName?: string | null;
  bankAccountNumber?: string | null;
  bankIfsc?: string | null;
  taxIdentifierLabel?: string | null;
  socialSecurityIdLabel?: string | null;
  pensionIdLabel?: string | null;

  notes?: string | null;
  publishedAt?: string | null;
  paidAt?: string | null;
  cancelledAt?: string | null;
  supersededAt?: string | null;

  /** Set on a replacement: the issued payslip this one corrects. */
  supersedesId?: string | null;
  supersedes?: PayslipLink | null;
  /** Set on an original that has been corrected: the payslip that replaced it. */
  supersededBy?: PayslipLink | null;

  // Delivery outcome — emailedAt is set only once the send actually succeeded.
  emailedAt?: string | null;
  emailTo?: string | null;
  emailAttempts?: number;
  emailError?: string | null;

  createdAt: string;
  updatedAt: string;
  items?: PayslipItem[];
  /** One-off lines added during a payroll run — the persisted input behind the adjustment items. */
  adjustments?: PayslipAdjustment[];
  payrollRunId?: string | null;
  employee?: { id: string; employeeCode: string; firstName: string; lastName: string; userId?: string | null };
  payrollRun?: { id: string; runNumber: string; status: PayrollRunStatus } | null;
}

/** One computed line of a preview — numbers, not strings, straight from the calculator. */
export interface PreviewLine {
  code: string;
  label: string;
  type: PayComponentType;
  amount: number;
  fullAmount: number;
  isTaxable: boolean;
  isStatutory: boolean;
  proratable: boolean;
  position: number;
}

/** POST /payslips/preview — the same maths as generating, but nothing is saved. */
export interface PayslipPreview {
  employee: { id: string; employeeCode: string; name: string; designation?: string | null };
  periodStart: string;
  periodEnd: string;
  payDate: string;
  currency: string;
  ctcAnnual: string;
  totalDays: number;
  paidDays: number;
  lopDays: number;
  factor: number;
  grossEarnings: number;
  totalDeductions: number;
  employerContributions: number;
  netPay: number;
  earnings: PreviewLine[];
  deductions: PreviewLine[];
  employerContribs: PreviewLine[];
  lines: PreviewLine[];
  /** Set when this employee already has a payslip for the month. */
  existingPayslip: { id: string; payslipNumber: string; status: PayslipStatus } | null;
}

export const PAYSLIP_STATUS_OPTIONS: { value: PayslipStatus; label: string }[] = [
  { value: 'DRAFT', label: 'Draft' },
  { value: 'PUBLISHED', label: 'Published' },
  { value: 'PAID', label: 'Paid' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'SUPERSEDED', label: 'Superseded' },
];

export const MONTH_OPTIONS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
].map((label, i) => ({ value: String(i + 1), label }));

// ── Payroll runs ────────────────────────────────────────────────────────────

export type PayrollRunStatus = 'DRAFT' | 'REVIEW' | 'APPROVED' | 'PAID' | 'CANCELLED';
export type PayslipAdjustmentType = 'EARNING' | 'DEDUCTION';

export interface PayslipAdjustment {
  id: string;
  payslipId: string;
  label: string;
  type: PayslipAdjustmentType;
  amount: string;
  notes?: string | null;
  createdAt: string;
}

export interface PayrollRun {
  id: string;
  runNumber: string;
  status: PayrollRunStatus;
  periodMonth: number;
  periodYear: number;
  periodStart: string;
  periodEnd: string;
  payDate: string;
  currency: string;
  employeeCount: number;
  totalGross: string;
  totalDeductions: string;
  totalEmployerCost: string;
  totalNetPay: string;
  notes?: string | null;
  expenseId?: string | null;
  accountId?: string | null;
  approvedAt?: string | null;
  paidAt?: string | null;
  cancelledAt?: string | null;
  createdAt: string;
  updatedAt: string;
  payslips?: Payslip[];
  createdBy?: { id: string; firstName: string; lastName: string } | null;
  approvedBy?: { id: string; firstName: string; lastName: string } | null;
  expense?: { id: string; title: string; amount: string; expenseDate: string; status: string } | null;
  account?: { id: string; name: string; balance: string; currency: string } | null;
  /** Only present on the create response — who was left out and why. */
  skipped?: Array<{ employeeCode: string; name: string; reason: string }>;
  failed?: Array<{ employeeCode: string; reason: string }>;
}

/** GET /payroll-runs/eligible */
export interface RunEligibility {
  periodMonth: number;
  periodYear: number;
  periodStart: string;
  periodEnd: string;
  payDate: string;
  totalDays: number;
  eligible: Array<{
    employeeId: string;
    employeeCode: string;
    name: string;
    designation?: string | null;
    currency: string | null;
    ctcAnnual: string | null;
  }>;
  skipped: Array<{ employeeCode: string; name: string; reason: string }>;
  currencies: string[];
  mixedCurrency: boolean;
}

export const PAYROLL_RUN_STATUS_OPTIONS: { value: PayrollRunStatus; label: string }[] = [
  { value: 'DRAFT', label: 'Draft' },
  { value: 'REVIEW', label: 'In review' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'PAID', label: 'Paid' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

export const PAYROLL_RUN_STATUS_STYLES: Record<string, string> = {
  DRAFT: 'bg-slate-500/15 text-slate-600 dark:text-slate-400',
  REVIEW: 'bg-amber-500/15 text-amber-600 dark:text-amber-400',
  APPROVED: 'bg-sky-500/15 text-sky-600 dark:text-sky-400',
  PAID: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
  CANCELLED: 'bg-red-500/15 text-red-600 dark:text-red-400',
};

/** GET /me/employee — the signed-in user's own record, minus anything sensitive. */
export interface MyEmployee {
  id: string;
  employeeCode: string;
  firstName: string;
  lastName: string;
  designation?: string | null;
  department?: string | null;
  dateOfJoining: string;
  status: EmployeeStatus;
}

/** Status colours for the employee's own payslip list (only issued statuses appear). */
export const PAYSLIP_STATUS_STYLES_SELF: Record<string, string> = {
  PUBLISHED: 'bg-sky-500/15 text-sky-600 dark:text-sky-400',
  PAID: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
};
